/**
 * OperationPathPicker — výběr DALŠÍHO KROKU zákaznické cesty grafem.
 *
 * Nahrazuje dřívější ``ChainOperationDropdown``, který tutéž volbu
 * podával jako plochý seznam přílepků („+ Přidat další operaci").
 * Nabídka je táž a pochází ze stejného místa — veřejného grafu tenanta
 * přes ``chain-continuations`` — ale rámuje se jako **cesta**: panel
 * nejdřív ukáže kroky, které už na položce stojí, a teprve pod nimi
 * větve, kterými cesta může pokračovat (spec_operations_graph § O5/1).
 *
 * Kandidáty NEsbírá sám: dostane je od rodiče
 * (``OperationPathSection``), který je má už kvůli tomu, aby věděl,
 * jestli se vůbec má CTA ukázat. Vlastní dotaz by byl druhá kopie téhož
 * klíče.
 *
 * Popisky větví (``display_name`` / ``description``) razí server —
 * jsou odvozené z veřejného grafu, takže sedí na Mapu výroby bez
 * druhé klientské resolvce.
 */

import { Button } from "@/ingot";
import { useState } from "react";

import type {
  AddOperationRequest,
  ChainContinuation,
  ItemOperation,
} from "@/api/types";

import { cx } from "@/lib/utils";

interface OperationPathPickerProps {
  /** Větve, kterými může cesta z aktuálního konce pokračovat. Rodič
   *  komponentu vůbec nemountuje, dokud je seznam prázdný. */
  candidates: ChainContinuation[];
  /** Kroky, které na cestě už stojí, v pořadí — primární operace plus
   *  dosud přidané. Vykreslí se nad nabídkou, aby bylo vidět, ODKUD
   *  se větví. */
  pathSoFar: string[];
  /** Zamčeno, dokud běží jiná mutace rodiče (typicky přidávání do
   *  košíku) — jinak by si obě sáhly na tutéž položku. */
  disabled?: boolean;
  isAdding?: boolean;
  /** Výchozí konfigurace zděděná z primární karty. Bez ní chain
   *  strategie cení krok proti ničemu a vrátí nulu. */
  defaultConfig?: Record<string, unknown>;
  onAddStep: (payload: AddOperationRequest) => Promise<ItemOperation>;
  onStepAdded?: (op: ItemOperation) => void;
  /** Přeloží odmítnutí serveru na větu pro zákazníka (KAN-1025). Bez ní
   *  by se selhání dalo jen zalogovat — a přesně to se dělo: odmítnutý
   *  ``POST /operations`` skončil jako ``Uncaught (in promise) ApiError``
   *  v konzoli a zákazník viděl tlačítko, které „nic nedělá". */
  formatError?: (err: unknown) => string;
  labels: {
    cta: string;
    pathSoFar: string;
    chooseNext: string;
  };
}

export function OperationPathPicker({
  candidates,
  pathSoFar,
  disabled = false,
  isAdding = false,
  defaultConfig,
  onAddStep,
  onStepAdded,
  formatError,
  labels,
}: OperationPathPickerProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  if (candidates.length === 0) return null;

  return (
    <div data-testid="operation-path-picker">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled || isAdding}
        onClick={() => {
          setAddError(null);
          setOpen((prev) => !prev);
        }}
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid="operation-path-toggle"
      >
        {labels.cta}
      </Button>

      {open && (
        <div
          className="mt-2 space-y-3 rounded-md border border-border bg-surface p-3 shadow-sm"
          data-testid="operation-path-menu"
        >
          {pathSoFar.length > 0 && (
            <div className="space-y-1" data-testid="operation-path-so-far">
              <p className="text-xs font-medium uppercase tracking-wide text-ink-3">
                {labels.pathSoFar}
              </p>
              <ol className="flex flex-wrap items-center gap-1 text-xs text-ink-2">
                {pathSoFar.map((step, i) => (
                  <li key={`${step}:${i}`} className="flex items-center gap-1">
                    {i > 0 && (
                      <span aria-hidden="true" className="text-ink-3">
                        →
                      </span>
                    )}
                    <span className="rounded bg-surface-2 px-2 py-0.5">
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-ink-3">
              {labels.chooseNext}
            </p>
            <div role="menu" className="space-y-1">
              {candidates.map((candidate) => (
                <button
                  key={`${candidate.name}:${candidate.template_node_id ?? candidate.slug}`}
                  type="button"
                  role="menuitem"
                  disabled={isAdding}
                  onClick={() => {
                    setAddError(null);
                    void onAddStep({
                      process_type: candidate.name,
                      template_node_id: candidate.template_node_id ?? null,
                      ...(defaultConfig ? { config: defaultConfig } : {}),
                    }).then(
                      (op) => {
                        setOpen(false);
                        onStepAdded?.(op);
                      },
                      (err: unknown) => {
                        // Menu zůstává otevřené: chyba patří k TÉ volbě,
                        // a zavřít ho by zákazníkovi vzalo i větev, u které
                        // se to nepovedlo, i možnost zkusit jinou.
                        setAddError(formatError?.(err) ?? null);
                      },
                    );
                  }}
                  className={cx(
                    "flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm transition",
                    "hover:bg-surface-2",
                    "disabled:cursor-not-allowed disabled:opacity-60",
                  )}
                  data-testid={`operation-path-add-${candidate.name}`}
                >
                  <span className="font-medium text-ink">
                    {candidate.display_name}
                  </span>
                  <span className="text-xs text-ink-3">
                    {candidate.description}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {addError !== null && (
            <p
              role="alert"
              className="rounded border border-danger-border bg-danger-bg px-3 py-2 text-xs text-danger"
              data-testid="operation-path-add-error"
            >
              {addError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
